/*
 * Structure Service
 * Only used to create lobby structure
 * Use Structure helper service for helper tasks
 * It uses single service --'lobby/get-lobbies-categories'-- to get all lobby structure json
 * Please refer to lobby interface for structure
 * Only make changes if related to structure of the json and getting data from it.
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { ConfigService } from '../config/config.service';
import { WebservicesService } from '../webservices/webservices.service';

import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { FavoritesService } from '../favorite/favorite.service';
import { UserSessionService } from '../user-session/user-session.service';
import { SessionManagerService } from '../session-manager/session-manager.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { API_LIB } from 'src/library/api.lib';
import { CategoryInterface, StructureInterface } from 'src/app/interfaces/lobby.interface';

type ApiInputObject = {
  clientapp?: string;
  customer_id?: number | string;
  include_games?: boolean;
  return_objects?: number;
  session_token?: string;
};

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class StructureService {

  private readonly structure = new BehaviorSubject<StructureInterface[]>([]);
  public structure$ = this.structure.asObservable();
  private categoryCache = new Map<string, Observable<PlatformResponse<StructureInterface[]>>>();

  constructor(
    private config: ConfigService,
    private webService: WebservicesService,
    private favoriteService: FavoritesService,
    private sessionManager: SessionManagerService,
    private userSession: UserSessionService,
  ) {
    this.sessionManager.loggedInState$.pipe(untilDestroyed(this)).subscribe(() => this.buildLobby());
    this.sessionManager.logout$.pipe(untilDestroyed(this)).subscribe(() => this.buildLobby());
  }

  //Please Read Comments at Top

  public buildLobby(): void {
    this.fetchCategories(true).pipe(
      tap(response => {

        const payload = response?.payload || [];
        this.structure.next(payload);
        if (this.userSession.isAuthenticated()) {
          const maincatgegories = payload.find(s => s.placement === 'MAIN')?.categoryInfo || [];
          this.favoriteService.loadInitialFavorites(maincatgegories);
        }

      }),
      catchError(err => {
        console.error('[StructureService] Error fetching lobby categories:', err);
        return of({ payload: [] });
      })
    ).subscribe();
  }

  private fetchCategories(includeGames: boolean): Observable<PlatformResponse<StructureInterface[]>> {

    return this.userSession.sessionState$.pipe(
      take(1),
      switchMap(({ sessionToken, customerId }) => {
        const cacheKey = `${includeGames}-${sessionToken ?? 'anon'}-${customerId ?? 'anon'}`;

        const cached = this.categoryCache.get(cacheKey);
        if (cached) {
          return cached;
        }
        const apiInput: ApiInputObject = {
          clientapp: this.config.getClientApp(),
          include_games: includeGames,
          ...(sessionToken && customerId ? {
            customer_id: customerId,
            session_token: sessionToken
          } : {})
        };

        const request$ = this.webService.get(API_LIB?.LOBBY_CATEGORIES, apiInput).pipe(
          shareReplay(1)
        );

        this.categoryCache.set(cacheKey, request$);
        return request$;
      })
    );
  }

  public getCategoriesByPlacement(placement: string): Observable<CategoryInterface[] | undefined> {
    return this.structure$.pipe(
      map(structures => structures.find(s => s.placement === placement)?.categoryInfo)
    );
  }

  public clearCategoryCache(): void {
    this.categoryCache.clear();
  }

}

//Please Read Comments at Top
